import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { SponsorStrip } from "@/components/festival/SponsorStrip";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Inloggen — Openluchtfilmfestival" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    let done = false;
    const go = async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user || done) return;
      done = true;
      try { await supabase.rpc("claim_admin_role"); } catch { /* ignore */ }
      navigate({ to: "/admin" });
    };
    void go();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") void go();
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    try { await supabase.rpc("claim_admin_role"); } catch { /* ignore */ }
    navigate({ to: "/admin" });
  }

  async function onGoogle() {
    setGoogleLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/auth",
    });
    if (result.error) {
      setGoogleLoading(false);
      toast.error("Inloggen met Google is niet gelukt.");
      return;
    }
    if (result.redirected) return;
    try { await supabase.rpc("claim_admin_role"); } catch { /* ignore */ }
    navigate({ to: "/admin" });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center px-5 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6 rounded-2xl border border-border bg-night-deep/60 p-8">
        <div>
          <p className="font-mono-cinema text-[10px] text-gold">— Admin</p>
          <h1 className="mt-2 font-display text-3xl font-medium">Inloggen</h1>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">E-mailadres</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Wachtwoord</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <Button type="submit" variant="hero" size="xl" disabled={loading} className="w-full">
          {loading ? "Bezig…" : "Inloggen"}
        </Button>
        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="font-mono-cinema text-[10px] uppercase tracking-[0.2em] text-foreground/40">of</span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <Button
          type="button"
          variant="outline"
          size="xl"
          disabled={googleLoading}
          onClick={onGoogle}
          className="w-full gap-3"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8Z" />
            <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3a7.2 7.2 0 0 1-10.7-3.8H1.3v3.1A12 12 0 0 0 12 24Z" />
            <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1Z" />
            <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.5-3.5A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8Z" />
          </svg>
          {googleLoading ? "Bezig…" : "Inloggen met Google"}
        </Button>
        </form>
      </div>
      <SponsorStrip />
      <Toaster position="top-center" />
    </div>
  );
}